import { useState, useEffect, useCallback } from 'react';
import { Capacitor } from '@capacitor/core';
import { Network, ConnectionStatus } from '@capacitor/network';

interface NetworkState {
  isOnline: boolean;
  connectionType: string | null;
}

const NETWORK_STATUS_KEY = 'orcamigo_network_status';

// Get initial status from localStorage for instant hydration
const getInitialStatus = (): NetworkState => {
  try {
    const cached = localStorage.getItem(NETWORK_STATUS_KEY);
    if (cached) {
      return JSON.parse(cached);
    }
  } catch (e) {
    // Ignore
  }
  // Default to online (optimistic)
  return { isOnline: true, connectionType: null };
};

export function useNetworkStatus() {
  const [networkState, setNetworkState] = useState<NetworkState>(getInitialStatus);
  const [listeners, setListeners] = useState<(() => void)[]>([]);

  // Update state and cache
  const updateNetworkState = useCallback((status: ConnectionStatus) => {
    const newState: NetworkState = {
      isOnline: status.connected,
      connectionType: status.connectionType,
    };
    setNetworkState(newState);
    
    // Cache for instant hydration on next load
    try {
      localStorage.setItem(NETWORK_STATUS_KEY, JSON.stringify(newState));
    } catch (e) {
      // Ignore storage errors
    }

    // Dispatch global event for components that need it
    window.dispatchEvent(new CustomEvent('network-status-change', { 
      detail: newState 
    }));
  }, []);

  useEffect(() => {
    let cleanupListener: (() => void) | null = null;

    const initNetwork = async () => {
      if (Capacitor.isNativePlatform()) {
        // Native: Use Capacitor Network plugin
        try {
          // Get initial status
          const status = await Network.getStatus();
          updateNetworkState(status);

          // Listen for changes
          const listener = await Network.addListener('networkStatusChange', updateNetworkState);
          cleanupListener = () => listener.remove();
        } catch (e) {
          console.error('Error initializing Capacitor Network:', e);
          // Fallback to browser API
          updateNetworkState({ connected: navigator.onLine, connectionType: 'unknown' });
        }
      } else {
        // Web: Use browser navigator.onLine
        updateNetworkState({ connected: navigator.onLine, connectionType: 'unknown' });

        const handleOnline = () => updateNetworkState({ connected: true, connectionType: 'unknown' });
        const handleOffline = () => updateNetworkState({ connected: false, connectionType: 'none' });

        window.addEventListener('online', handleOnline);
        window.addEventListener('offline', handleOffline);

        cleanupListener = () => {
          window.removeEventListener('online', handleOnline);
          window.removeEventListener('offline', handleOffline);
        };
      }
    };

    initNetwork();

    return () => {
      if (cleanupListener) {
        cleanupListener();
      }
    };
  }, [updateNetworkState]);

  // Register callback for when network comes back online
  const onOnline = useCallback((callback: () => void) => {
    setListeners((prev) => [...prev, callback]);
    return () => setListeners((prev) => prev.filter((cb) => cb !== callback));
  }, []);

  // Trigger all registered callbacks when going online
  useEffect(() => {
    if (networkState.isOnline && listeners.length > 0) {
      listeners.forEach((cb) => {
        try {
          cb();
        } catch (e) {
          console.error('Error in network online callback:', e);
        }
      });
    }
  }, [networkState.isOnline, listeners]);

  return {
    isOnline: networkState.isOnline,
    connectionType: networkState.connectionType,
    onOnline,
  };
}

// Simple check without hook (for use in non-component code)
export async function checkNetworkStatus(): Promise<boolean> {
  if (Capacitor.isNativePlatform()) {
    try {
      const status = await Network.getStatus();
      return status.connected;
    } catch (e) {
      return navigator.onLine;
    }
  }
  return navigator.onLine;
}
